/**
 * Puts a kept simulator run back on screen: the thread it sent and its reply, the reply's
 * tool calls as its timeline, its run details, and the sidebar it ran with. Asking again
 * continues it as a new conversation.
 */

import { atom } from 'jotai';
import { generateMessageId } from '@/hooks/use-chat';
import type { SimulatorRun } from '@/services/simulator-run.service';
import { chatMessagesAtom } from '@/stores/chat.store';
import {
  lastRunDetailsAtom,
  lastRunErrorAtom,
  resetSimulatorAtom,
  runDetailsFrom,
  thinkingStepsAtom,
  type ThinkingStep,
} from '@/stores/websocket.store';
import type { ChatMessage } from '@/types/websocket.types';
import {
  EMPTY_SIMULATION_PROFILE,
  openSectionsAtom,
  simulateToolsAtom,
  simulationProfileAtom,
} from '../sidebar/simulation-profile.store';
import { filledSections } from './scenario-case';
import { channelIdAtom, describedScenarioAtom, reopenedLinksAtom, runOnScreenAtom } from './simulator-session.store';

const ranAt = (run: SimulatorRun): number => Date.parse(run.createdAt) || Date.now();

/** Earlier replies come back as the agent turns they became; the run's own reply, or its error, ends the thread. */
export const threadOf = (run: SimulatorRun): ChatMessage[] => {
  const at = ranAt(run);
  const turns: ChatMessage[] = run.query.map((turn) => ({
    id: generateMessageId(),
    from: turn.role === 'user' ? 'user' : 'assistant',
    content: turn.text,
    timestamp: at,
    ...(turn.role === 'user' && run.origin.channelId ? { channelId: run.origin.channelId } : {}),
  }));
  const answer = run.response || run.error;
  if (!answer) return turns;
  const reply: ChatMessage = {
    id: generateMessageId(),
    from: 'assistant',
    content: answer,
    timestamp: at,
    fromRun: true,
    ...(run.response ? { requestId: run.requestId } : { isError: true }),
  };
  return [...turns, reply];
};

export const timelineOf = (run: SimulatorRun): ThinkingStep[] =>
  run.toolCalls.map((call, index) => ({
    nodeId: `${run.requestId}:${index}`,
    nodeType: 'tool',
    label: call.name,
    timestamp: ranAt(run),
    requestId: run.requestId,
    toolName: call.name,
    toolArgs: call.args,
    toolOutput: call.output,
    durationMs: call.durationMs ?? undefined,
    outcome: call.outcome ?? undefined,
    simulated: call.simulated,
    simulatedWrite: call.simulatedWrite,
    grounded: call.grounded,
    isWrite: call.simulatedWrite || undefined,
    done: true,
  }));

/** Replaces the screen with the run in its tool mode; the reset gives it a fresh conversation id. */
export const restoreRunAtom = atom(null, (_get, set, run: SimulatorRun) => {
  const profile = { ...EMPTY_SIMULATION_PROFILE, ...run.simulationProfile };

  set(resetSimulatorAtom);
  set(runOnScreenAtom, run.requestId);
  set(reopenedLinksAtom, { sopId: run.sopId, scenarioId: run.scenarioId, ticketId: run.ticketId, kind: run.kind });
  // A described run's type is kept per tab with its thread and world, as one described here is.
  if (run.scenarioType) set(describedScenarioAtom, { situation: '', scenarioType: run.scenarioType });
  set(channelIdAtom, run.origin.channelId ?? '');
  set(simulationProfileAtom, profile);
  set(simulateToolsAtom, run.mode === 'simulated');
  set(openSectionsAtom, [...filledSections(profile), ...(run.ticketId ? ['conversation'] : [])]);
  set(chatMessagesAtom, threadOf(run));
  set(thinkingStepsAtom, timelineOf(run));
  set(lastRunDetailsAtom, run.details ? runDetailsFrom(run.details, run.requestId) : null);
  set(lastRunErrorAtom, run.response ? null : run.error);
});
